"use client";

import { useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { getCategoryPreviewImage } from "@/lib/catalog";
import { fixMaybeBrokenText } from "@/lib/text-client";

type CategoryScrollerItem = {
  slug: string;
  name: string;
  productCount?: number;
  previewImage?: string;
};

type CategoryScrollerProps = {
  items: CategoryScrollerItem[];
  title: string;
  description?: string;
  actionLabel?: string;
  actionHref?: string;
  className?: string;
  itemClassName?: string;
  compact?: boolean;
};

export function CategoryScroller({
  items,
  title,
  description,
  actionLabel,
  actionHref,
  className = "",
  itemClassName = "",
  compact = false
}: CategoryScrollerProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);

  if (!items.length) {
    return null;
  }

  function scrollTrack(direction: -1 | 1) {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    const amount = Math.max(260, Math.round(track.clientWidth * 0.84)) * direction;
    track.scrollBy({ left: amount, behavior: "smooth" });
  }

  return (
    <section className={`category-scroller ${compact ? "category-scroller--compact" : ""} ${className}`.trim()}>
      <div className="category-scroller__header">
        <div className="category-scroller__copy">
          <h2 className="category-scroller__title">{fixMaybeBrokenText(title)}</h2>
          {description ? (
            <p className="category-scroller__description">{fixMaybeBrokenText(description)}</p>
          ) : null}
        </div>

        <div className="category-scroller__actions">
          {actionLabel && actionHref ? (
            <Link className="category-scroller__link" href={actionHref}>
              {fixMaybeBrokenText(actionLabel)}
            </Link>
          ) : null}

          <div className="category-scroller__controls" aria-label="Навигация по категориям">
            <button
              aria-label="Прокрутить категории назад"
              className="category-scroller__control"
              onClick={() => scrollTrack(-1)}
              type="button"
            >
              <svg fill="none" viewBox="0 0 24 24">
                <path
                  d="m14.5 6.5-5 5 5 5"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.2"
                />
              </svg>
            </button>
            <button
              aria-label="Прокрутить категории вперед"
              className="category-scroller__control"
              onClick={() => scrollTrack(1)}
              type="button"
            >
              <svg fill="none" viewBox="0 0 24 24">
                <path
                  d="m9.5 6.5 5 5-5 5"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.2"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="category-scroller__track" ref={trackRef}>
        {items.map((item) => (
          <Link
            className={`category-scroller__item ${compact ? "category-scroller__item--compact" : ""} ${itemClassName}`.trim()}
            href={`/category/${item.slug}`}
            key={item.slug}
          >
            <span className="category-scroller__item-media">
              <Image
                alt={fixMaybeBrokenText(item.name)}
                height={120}
                loading="lazy"
                quality={78}
                sizes="(max-width: 860px) 96px, 120px"
                src={item.previewImage || getCategoryPreviewImage(item.slug)}
                width={120}
              />
            </span>

            <span className="category-scroller__item-copy">
              <strong>{fixMaybeBrokenText(item.name)}</strong>
              {typeof item.productCount === "number" ? (
                <span>{item.productCount.toLocaleString("ru-RU")} товаров</span>
              ) : null}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
